import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import AdminLoginModal from './AdminLogin/AdminLoginModal';
import { getAdminToken, getCorporateToken, getIndividualToken } from '../utils/auth';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requireAdmin?: boolean;
    requireCorporate?: boolean;
    requireIndividual?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    children,
    requireAdmin = false,
    requireCorporate = false,
    requireIndividual = false,
}) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (requireAdmin) {
            const adminToken = getAdminToken();
            const adminUser = localStorage.getItem('admin_user');
            if (adminToken && adminUser) {
                setIsAuthenticated(true);
            } else {
                setIsAuthenticated(false);
                setShowLoginModal(true);
            }
        } else if (requireCorporate) {
            if (getCorporateToken()) {
                setIsAuthenticated(true);
            } else {
                setIsAuthenticated(false);
            }
        } else if (requireIndividual) {
            if (getIndividualToken()) {
                setIsAuthenticated(true);
            } else {
                setIsAuthenticated(false);
            }
        } else {
            // For non-protected routes, allow access
            setIsAuthenticated(true);
        }
    }, [requireAdmin, requireCorporate, requireIndividual]);

    const handleLoginSuccess = () => {
        if (requireAdmin) {
            const adminToken = getAdminToken();
            const adminUser = localStorage.getItem('admin_user');
            if (adminToken && adminUser) {
                setIsAuthenticated(true);
                setShowLoginModal(false);
                return;
            }
        }
        setShowLoginModal(false);
    };

    const handleLoginModalClose = () => {
        setShowLoginModal(false);
        // Redirect to home if modal is closed without logging in
        navigate('/', { replace: true });
    };

    // Loading state while checking authentication
    if (isAuthenticated === null) {
        return (
            <div
                className="min-h-screen bg-gray-50 flex items-center justify-center"
                role="status"
                aria-live="polite"
                aria-label="Checking authentication"
            >
                <div className="text-center">
                    <div
                        className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"
                        aria-hidden="true"
                    />
                    <span className="sr-only">Checking authentication...</span>
                    <p className="mt-4 text-gray-600">Checking authentication...</p>
                </div>
            </div>
        );
    }

    // Show login modal if not authenticated
    if (!isAuthenticated && showLoginModal) {
        return (
            <AdminLoginModal
                isOpen={showLoginModal}
                onClose={handleLoginModalClose}
                onLoginSuccess={handleLoginSuccess}
            />
        );
    }

    // If not authenticated and modal is closed, redirect appropriately
    if (!isAuthenticated) {
        if (requireCorporate) {
            return <Navigate to="/login/corporate" replace />;
        }
        if (requireIndividual) {
            const currentPath = window.location.pathname;
            return <Navigate to={`/login?returnUrl=${encodeURIComponent(currentPath)}`} replace />;
        }
        return <Navigate to="/" replace />;
    }

    // Render protected content if authenticated
    return <>{children}</>;
};

export default ProtectedRoute;
